/**
 * PdoConceptChips — small clickable chips listing the PDO concepts a
 * rule is grounded in.
 *
 * Phase C Workstream 3 §3.2 originally suggested reusing
 * <CausalNarrativeDisplay /> from the Strategy page. That component
 * expects an annotated narrative shape (prose + per-phrase causal
 * edges with weights). A TacticalRule has only the unannotated
 * `rationale` string + flat `pdo_concept_refs` / `pdo_concept_ids`
 * lists, so we render concept chips here. A future Phase D
 * enhancement could have the cycle generate annotations during PDO
 * alignment, at which point the rule detail can swap to the richer
 * CausalNarrativeDisplay component without changing this surface.
 */

import { clsx } from 'clsx';

interface PdoConceptChipsProps {
  conceptRefs: string[];
  conceptIds: string[];
  /** Compact variant for the rule-card grid */
  size?: 'sm' | 'md';
  /** Optional click handler (e.g. filter rules by concept) */
  onClickConcept?: (conceptId: string, conceptRef: string) => void;
}

export function PdoConceptChips({
  conceptRefs,
  conceptIds,
  size = 'md',
  onClickConcept,
}: PdoConceptChipsProps) {
  if (!conceptRefs.length) return null;
  const padding = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const text = size === 'sm' ? 'text-[10px]' : 'text-[11px]';

  return (
    <div className="flex flex-wrap gap-1">
      {conceptRefs.map((ref, idx) => {
        const id = conceptIds[idx] || ref;
        const ChipTag = onClickConcept ? 'button' : 'span';
        return (
          <ChipTag
            key={`${id}-${idx}`}
            type={onClickConcept ? 'button' : undefined}
            onClick={
              onClickConcept ? () => onClickConcept(id, ref) : undefined
            }
            title={id}
            className={clsx(
              'rounded-full border border-sky-200 bg-sky-50 font-medium text-sky-700 transition',
              padding,
              text,
              onClickConcept && 'cursor-pointer hover:bg-sky-100',
            )}
          >
            {ref}
          </ChipTag>
        );
      })}
    </div>
  );
}
